import { Injectable, signal } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import {
    InfoLotChip,
    LegTourPointConfig,
    PickupPlanningTransportOrderEntityType,
    TourPointConfig
} from '../../models/planningPageTypes';
import { protectSubscription } from '@nagel-utils';
import {
    DRIVE_INSTRUCTION_SHIPMENT_CHIPS,
    DRIVE_INSTRUCTION_TOUR_POINT_CHIPS,
} from '../configuration/consts/pickupPlanning';
import { environment } from 'apps/nagel-cal-disposition/environment/environment';
import { pickupPlanningDriveInstructionsWorkflowEndpoint } from '../configuration/consts/endpoints';
import { RequestService, SnackbarService } from '@nagel-services';
import { AssignmentRequestsService } from './assignment-requests.service';
import { SUCCESS_SNACKBAR, ERROR_SNACKBAR, INFO_SNACKBAR, SnackbarType } from '@nagel-types';
import { LotLegResponse, UnassignLegsResponse } from '../../models/assignmentsTypes';
import {
    AppChipStrategy,
    CommonChipStrategy,
    ProductGroupsChipStrategy,
    ShipmentDateChipStrategy,
    TourPointDateChipStrategy
} from './drive-instuctions-chips-strategy';
import { RESPONSE_CODES } from '@nagel-consts';
import { WARNING_SNACKBAR } from 'libs/nagel-types/src/lib/snackbarTypes';

const shouldEmitSubscriptionOnce = true;

@Injectable({
    providedIn: 'root'
})
export class DriveInstructionsDrawerService {
    public isDrawerOpened: BehaviorSubject<boolean> = new BehaviorSubject(false);
    public isDrawerDetailsSectionOpened: BehaviorSubject<boolean> = new BehaviorSubject(false);
    public transportOrderDataSubject: BehaviorSubject<PickupPlanningTransportOrderEntityType> =
        new BehaviorSubject<PickupPlanningTransportOrderEntityType>({} as PickupPlanningTransportOrderEntityType);

    public selectedTourPointSubject: BehaviorSubject<TourPointConfig | null> = new BehaviorSubject<TourPointConfig | null>(null);

    public tourPointsSubject: BehaviorSubject<TourPointConfig[]> = new BehaviorSubject<TourPointConfig[]>([]);
    public tourPointDetailsSubject: BehaviorSubject<LegTourPointConfig[]> = new BehaviorSubject<LegTourPointConfig[]>([]);
    public isLoadingTourPointsSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    public isSilentUpdateInProgress = signal<boolean>(false);

    public refreshTriggerSubject: BehaviorSubject<number> = new BehaviorSubject<number>(0);
    public refreshTriggerObservable$ = this.refreshTriggerSubject.asObservable();

    public modifiedTransportOrderId = signal<number | null>(null);
    public isUnassigningTriggered = signal<boolean>(false);

    constructor(
        private requestService: RequestService,
        private assignmentRequestsService: AssignmentRequestsService,
        private snackbarService: SnackbarService
    ) { }

    public toggleDrawer(transportOrder: PickupPlanningTransportOrderEntityType) {
        this.isDrawerDetailsSectionOpened.next(false);
        this.transportOrderDataSubject.next(transportOrder);
        this.setDrawerOpened(true);
        this.modifiedTransportOrderId.set(null);
        this.isUnassigningTriggered.set(false);
    }

    setDrawerOpened(opened: boolean): void {
        this.isDrawerOpened.next(opened);
    }

    setDrawerDetailsSectionOpened(opened: boolean): void {
        this.isDrawerDetailsSectionOpened.next(opened);
    }

    setSelectedTourPoint(tourPoint: TourPointConfig | null): void {
        this.selectedTourPointSubject.next(tourPoint);
    }

    executeGetTourPoints(transportOrderId: number,) {
        const url = `${environment.apiUrl}${pickupPlanningDriveInstructionsWorkflowEndpoint}?transportOrderId=${transportOrderId}`;

        return this.requestService.getRequest<unknown, TourPointConfig[]>(url, {});
    }

    public getTourPointsData(transportOrderId: number, showSpinner: boolean = true):void {
        if (showSpinner) {
            this.isLoadingTourPointsSubject.next(true);
        } else {
            this.isSilentUpdateInProgress.set(true);
        }

        const requestObservable = this.executeGetTourPoints(transportOrderId);

        protectSubscription<TourPointConfig[]>(
            requestObservable,
            this.onTourPointsRetrievalSuccess,
            this.onTourPointsRetrievalError,
            true
        );
    }

    public getTourPointDetails(tourPoint: TourPointConfig): void {
        const expandedLegCards = (tourPoint.legCards || []).map((leg: LegTourPointConfig, index: number) => ({
            ...leg,
            infoChips: this.getShipmentInfoChips(leg),
            order: index + 1
        }));

        this.tourPointDetailsSubject.next(expandedLegCards);
    }

    private getMappedTourPointInfoChipKey(key: string): string {
        const keyMap: Record<string, string> = {
            'plannedArrivalTime': DRIVE_INSTRUCTION_TOUR_POINT_CHIPS.DELIVERY_DATE_FROM,
            'weight': DRIVE_INSTRUCTION_TOUR_POINT_CHIPS.WEIGHT,
            'floorPalletSpaces': DRIVE_INSTRUCTION_TOUR_POINT_CHIPS.FLOOR_PALLET_SPACES,
            'volumePalletSpaces': DRIVE_INSTRUCTION_TOUR_POINT_CHIPS.VOLUME_PALLET_SPACES,
            'uniqueClientsCount': DRIVE_INSTRUCTION_TOUR_POINT_CHIPS.PEOPLE,
            'uniqueTrafficFlows': DRIVE_INSTRUCTION_TOUR_POINT_CHIPS.TRAFFIC_FLOW
        };

        return keyMap[key] || key;
    }

    private getMappedShipmentInfoChipKey(key: string): string {
        const keyMap: Record<string, string> = {
            'fixedDeliveryDate': DRIVE_INSTRUCTION_SHIPMENT_CHIPS.FIXED_DELIVERY_DATE_FROM,
            'fixedDeliveryTime': DRIVE_INSTRUCTION_SHIPMENT_CHIPS.FIXED_DELIVERY_TIME_FROM,
            'weight': DRIVE_INSTRUCTION_SHIPMENT_CHIPS.WEIGHT,
            'floorPalletSpaces': DRIVE_INSTRUCTION_SHIPMENT_CHIPS.FLOOR_PALLET_SPACES,
            'volumePalletSpaces': DRIVE_INSTRUCTION_SHIPMENT_CHIPS.VOLUME_PALLET_SPACES,
            'trafficMode': DRIVE_INSTRUCTION_SHIPMENT_CHIPS.TRAFFIC_FLOW
        };

        return keyMap[key] || key;
    }

    private DRIVE_INSTRUCTION_TOUR_POINT_CHIPS_STRATEGIES_MAP = [
        new TourPointDateChipStrategy(),
        new CommonChipStrategy(),
        new ProductGroupsChipStrategy()
    ];

    private DRIVE_INSTRUCTION_SHIPMENT_CHIPS_STRATEGIES_MAP = [
        new ShipmentDateChipStrategy(),
        new CommonChipStrategy(),
        new ProductGroupsChipStrategy()
    ];

    private getStrategyForChip = (
        strategiesMapKey: AppChipStrategy[],
        key: string,
        value: unknown,
        keysMap: Record<string, string>
    ): AppChipStrategy | null => {
        const applicableStrategy = strategiesMapKey.find(strategy => strategy.isApplicable(key, value, keysMap));

        return applicableStrategy || null;
    };

    getTourPointInfoChips = (tourPoint: TourPointConfig): InfoLotChip[] => {
        return Object.entries(tourPoint).flatMap(([key, value]) => {
            const mappedKey = this.getMappedTourPointInfoChipKey(key);
            let chipsMap = DRIVE_INSTRUCTION_TOUR_POINT_CHIPS as Record<string, string>;

            if (tourPoint.legCards && tourPoint.legCards.length === 0) {
                const { PEOPLE, TRAFFIC_FLOW, ...ADJUSTED_DRIVE_INSTRUCTIONS_CHIPS } = DRIVE_INSTRUCTION_TOUR_POINT_CHIPS;

                chipsMap = ADJUSTED_DRIVE_INSTRUCTIONS_CHIPS as Record<string, string>;
            }

            const strategy = this.getStrategyForChip(
                this.DRIVE_INSTRUCTION_TOUR_POINT_CHIPS_STRATEGIES_MAP,
                mappedKey,
                value,
                chipsMap
            );

            if (strategy) {
                return strategy.applyStrategy(mappedKey, value);
            }

            return [];
        });
    };

    getShipmentInfoChips = (shipment: LegTourPointConfig): InfoLotChip[] => {
        return Object.entries(shipment).flatMap(([key, value]) => {
            const mappedKey = this.getMappedShipmentInfoChipKey(key);

            const strategy = this.getStrategyForChip(
                this.DRIVE_INSTRUCTION_SHIPMENT_CHIPS_STRATEGIES_MAP,
                mappedKey,
                value,
                DRIVE_INSTRUCTION_SHIPMENT_CHIPS
            );

            if (strategy) {
                return strategy.applyStrategy(mappedKey, value);
            }

            return [];
        });
    };

    private onTourPointsRetrievalSuccess = (response: TourPointConfig[]): void => {
        this.tourPointsSubject.next(response.map(tourPoint => ({
            ...tourPoint,
            infoChips: this.getTourPointInfoChips(tourPoint)
        })));

        if (this.selectedTourPointSubject.value) {
            const updatedSelectedTourPoint = response.find(tp => tp.tourpointId === this.selectedTourPointSubject.value?.tourpointId) || null;

            if (updatedSelectedTourPoint) {
                this.setSelectedTourPoint(updatedSelectedTourPoint);
                this.getTourPointDetails(updatedSelectedTourPoint);
            }
        }
        this.isLoadingTourPointsSubject.next(false);
        this.isSilentUpdateInProgress.set(false);
    };

    private onTourPointsRetrievalError = (error: unknown): void => {
        console.error('Error retrieving tour points:', error);
        this.isLoadingTourPointsSubject.next(false);
        this.isSilentUpdateInProgress.set(false);
    };

    unassignLegsAndLotsFromTransportOrder(selectedLegs: LegTourPointConfig[], onSuccess: (() => void)) {
        const legIds = selectedLegs.map(leg => leg.legId);
        const selectedLegsIds = legIds.length ? legIds : [];
        const {transportOrderId} = this.transportOrderDataSubject.value;

        protectSubscription(
            this.assignmentRequestsService.unassignLegsAndLotsFromTransportOrder(selectedLegsIds, transportOrderId),
            (data) => this.onUnassignRetrievalSuccess(data, onSuccess),
            (error) => this.onUnassignRetrievalFailure((error as Error).message),
            shouldEmitSubscriptionOnce
        );
    }

    onUnassignRetrievalSuccess = (requestResponse: UnassignLegsResponse, onSuccess: (() => void)) => {
        const { message, typeSnackbar } = this.getMessageForUnassignLegsAndLots(requestResponse);

        const { LegsResponse } = requestResponse.Value;

        const hasConflicts = LegsResponse.some(leg => leg.StatusCode === RESPONSE_CODES.CONFLICT);

        if (hasConflicts) {
            this.handleLegUnassignConflicts(LegsResponse);
            this.retrieveModifiedTransportOrderId();
        }

        this.snackbarService.pushSnackbar({
            message,
            action: 'OK',
            type: typeSnackbar
        });

        if (typeSnackbar === SUCCESS_SNACKBAR || typeSnackbar === INFO_SNACKBAR) {
            onSuccess && onSuccess();
        }
    };

    private handleLegUnassignConflicts = (responses: LotLegResponse[]) => {
        for (const response of responses) {
            if (response.StatusCode !== RESPONSE_CODES.CONFLICT) {
                continue;
            }

            if (response.Success) {
                this.snackbarService.pushSnackbar({
                    message: $localize`The unassigning process affected an out-of-sync item - leg with ID ${response.Id}. 
                        The issue has been resolved automatically. Please refresh the page.`,
                    action: 'OK',
                    type: WARNING_SNACKBAR,
                    persistent: true
                });
            } else {
                this.snackbarService.pushSnackbar({
                    message: $localize`Conflict detected on leg ${response.Id}.
                        Some local data could not be cleaned up. Our team has been notified. Log ID: ${response.IncidentId}.
                        This Log ID will no longer be accessible after dismissing this message.`,
                    action: 'OK',
                    type: ERROR_SNACKBAR,
                    persistent: true
                });
            }
        }
    };

    onUnassignRetrievalFailure = (message: string) => {
        this.snackbarService.pushSnackbar({
            message,
            action: 'OK',
            type: ERROR_SNACKBAR
        });
    };

    private calculateNumberOperationsPassed(requestResponse: UnassignLegsResponse) {
        let numberLegsPassed = 0;
        const totalLegsOperations = requestResponse.Value.LegsResponse.length;

        numberLegsPassed = requestResponse.Value.LegsResponse.filter(leg => leg.Success && leg.StatusCode !== RESPONSE_CODES.CONFLICT).length;

        return { numberLegsPassed, totalLegsOperations };
    }

    private getMessageForUnassignLegsAndLots(requestResponse: UnassignLegsResponse) {
        let message;
        let typeSnackbar: SnackbarType;

        const { numberLegsPassed, totalLegsOperations } = this.calculateNumberOperationsPassed(requestResponse);

        if (numberLegsPassed === totalLegsOperations) {
            message = $localize`Selected items have been removed.`;
            typeSnackbar = SUCCESS_SNACKBAR;
        } else if (numberLegsPassed === 0) {
            message = $localize`Selected items could not be removed.`;
            typeSnackbar = ERROR_SNACKBAR;
        } else {
            message = $localize`${numberLegsPassed} of ${totalLegsOperations} elements were removed.`;
            typeSnackbar = INFO_SNACKBAR;
        }

        return { message, typeSnackbar };
    }

    retrieveModifiedTransportOrderId() {
        this.modifiedTransportOrderId.set(this.transportOrderDataSubject.value.transportOrderId);
        this.getTourPointsData(this.transportOrderDataSubject.value.transportOrderId, false);
        this.refreshTriggerSubject.next(this.refreshTriggerSubject.value + 1);

    }

    isFromThirdPartySystem = (tourPoint: TourPointConfig): boolean => {
        return tourPoint.legCards ? tourPoint.legCards.length === 0 : false;
    };
}